<template>
  <div class="knowhow" v-if="posts.length">
    <div class="renew3_wrapper knowhow_wrapper">
      <h3 class="knowhow_h3">お役立ちコラム</h3>

      <div class="flex knowhow_list">
        <a
          v-for="post in posts"
          :key="post.id"
          :href="post.link"
          target="_blank"
          rel="noopener noreferrer"
          class="knowhow_item"
        >
          <div class="knowhow_thumb">
            <img
              v-if="thumbOf(post)"
              :src="thumbOf(post)"
              :alt="post.title.rendered"
            />
          </div>
          <p class="knowhow_date">{{ formatDate(post.date) }}</p>
          <h4 class="knowhow_title" v-html="post.title.rendered"></h4>
        </a>
      </div>

      <div class="knowhow_more">
        <a
          :href="ARCHIVE_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="format_more_btn"
          >＞記事をもっと見る</a
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * トップページの「お役立ちコラム」。ポータル(WordPress)から3件取って並べる。
 *
 * Top.vue（1,700行超）の分解で切り出した。取得も整形もこの中で完結するので、
 * 親は <top-knowhow-list /> を置くだけでよい。
 *
 * ⚠️ 中身はビルド時に存在しない API 取得コンテンツなので、Top.vue 側では
 * <!-- static:skip --> で囲んで静的トップ(public/top-content.html)から外す。
 * 外さないと見出しだけのガワが全ユーザーに焼かれる。
 */
import axios from 'axios'
import { onMounted, ref } from 'vue'

/** WordPress の /know-how/ カテゴリ。常に本番ポータルを参照する */
const PORTAL_BASE = 'https://portal.yagish.jp'
const KNOWHOW_CATEGORY_ID = 58
const ARCHIVE_URL = `${PORTAL_BASE}/know-how/`

type WpMedia = {
  source_url?: string
  media_details?: { sizes?: { medium?: { source_url?: string } } }
}

type WpPost = {
  id: number
  link: string
  date: string
  title: { rendered: string }
  _embedded?: { 'wp:featuredmedia'?: WpMedia[] }
}

const posts = ref<WpPost[]>([])

/** アイキャッチ。medium があればそれ、無ければ原寸 */
function thumbOf(post: WpPost): string {
  const media = post._embedded && post._embedded['wp:featuredmedia']
  if (media && media[0]) {
    const sizes = media[0].media_details && media[0].media_details.sizes
    if (sizes && sizes.medium) return sizes.medium.source_url || ''
    return media[0].source_url || ''
  }
  return ''
}

function formatDate(d: string): string {
  return d ? d.slice(0, 10).replace(/-/g, '.') : ''
}

onMounted(() => {
  axios
    .get<WpPost[]>(`${PORTAL_BASE}/wp-json/wp/v2/posts`, {
      params: { categories: KNOWHOW_CATEGORY_ID, per_page: 3, _embed: true },
      timeout: 60000
    })
    .then((response) => {
      posts.value = response.data
    })
    .catch((error) => {
      // 取得できなくてもトップは出す（v-if で丸ごと消える）
      console.error('お役立ちコラムの取得エラー', error)
    })
})
</script>

<style lang="scss" scoped>
/* お役立ちコラム */
.renew3_top .knowhow {
  margin-bottom: 60px;
}

.renew3_top .knowhow_h3 {
  text-align: center;
  font-weight: bold;
  font-size: 24px;
  color: #004d40;
  margin-bottom: 40px;
}

.renew3_top .knowhow_list {
  flex-wrap: wrap;
  gap: 24px;
}

.renew3_top .knowhow_item {
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s;
}

.renew3_top .knowhow_item:hover {
  transform: translateY(-4px);
  opacity: 1;
  text-decoration: none;
}

.renew3_top .knowhow_thumb {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  background: #eef5e8;
  margin-bottom: 12px;
}

.renew3_top .knowhow_thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.renew3_top .knowhow_date {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
}

.renew3_top .knowhow_title {
  font-size: 15px;
  font-weight: bold;
  line-height: 1.5;
  color: #1a1a1a;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.renew3_top .knowhow_more {
  text-align: right;
  margin-top: 24px;
}

/* SP */
@media screen and (width <= 640px) {
  /* お役立ちコラム */
  .renew3_top .knowhow_list {
    flex-direction: column;
  }
}
</style>
